(() => {
  if (window.__maisonHowItWorksLoaded) return;
  window.__maisonHowItWorksLoaded = true;
  const instances = new Map();

  function initHowItWorks(root, mainSwiper) {
    const trigger = root.querySelector('[data-maison-how-open]');
    const dialog = root.querySelector('[data-maison-how-dialog]');
    if (!trigger || !dialog || typeof dialog.showModal !== 'function') return () => {};

    const controller = new AbortController();
    const { signal } = controller;
    const content = dialog.querySelector('[data-maison-how-content]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let enterFrame;
    let closeSequence = 0;
    let closing = false;
    let bodyStyles;
    let scrollPosition;
    let keyboardWasEnabled = false;
    let pointerStartedOutside = false;

    const restorePage = (restoreFocus = true) => {
      cancelAnimationFrame(enterFrame);
      closeSequence += 1;
      closing = false;
      delete dialog.dataset.visible;
      delete dialog.dataset.opening;
      if (!bodyStyles) return;

      for (const [property, value, priority] of bodyStyles) {
        if (value) document.body.style.setProperty(property, value, priority);
        else document.body.style.removeProperty(property);
      }
      bodyStyles = undefined;
      window.scrollTo({ ...scrollPosition, behavior: 'instant' });
      if (keyboardWasEnabled) mainSwiper?.keyboard?.enable();
      if (restoreFocus && trigger.isConnected) trigger.focus({ preventScroll: true });
    };

    const finishClose = () => {
      if (dialog.open) dialog.close();
      restorePage();
    };

    const close = () => {
      if (!dialog.open || closing) return;
      closing = true;
      cancelAnimationFrame(enterFrame);
      delete dialog.dataset.opening;
      delete dialog.dataset.visible;
      const transitions = dialog.getAnimations();
      if (reducedMotion.matches || !transitions.length) {
        finishClose();
        return;
      }
      const sequence = ++closeSequence;
      // Follow the actual CSS transition, including interrupted or slowed motion.
      Promise.allSettled(transitions.map((transition) => transition.finished)).then(() => {
        if (closing && sequence === closeSequence) finishClose();
      });
    };

    trigger.addEventListener('click', () => {
      if (dialog.open || !trigger.getClientRects().length) return;
      scrollPosition = { top: window.scrollY, left: window.scrollX };
      bodyStyles = ['position', 'top', 'width'].map((property) => [
        property,
        document.body.style.getPropertyValue(property),
        document.body.style.getPropertyPriority(property),
      ]);
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollPosition.top}px`;
      document.body.style.width = '100%';
      keyboardWasEnabled = Boolean(mainSwiper?.keyboard?.enabled);
      mainSwiper?.keyboard?.disable();
      dialog.dataset.opening = '';
      dialog.showModal();
      content.scrollTop = 0;
      // Commit the starting position before enabling the interruptible transition.
      void dialog.offsetHeight;
      enterFrame = requestAnimationFrame(() => {
        delete dialog.dataset.opening;
        dialog.dataset.visible = '';
      });
    }, { signal });

    dialog.querySelectorAll('[data-maison-how-close]').forEach((button) => {
      button.addEventListener('click', close, { signal });
    });
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      close();
    }, { signal });
    dialog.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const controls = [...dialog.querySelectorAll('button:not([disabled]), a[href], [tabindex="0"]')]
        .filter((element) => element.getClientRects().length);
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }, { signal });
    dialog.addEventListener('close', () => {
      if (!dialog.open) restorePage();
    }, { signal });

    const isOutside = (event) => {
      const bounds = dialog.getBoundingClientRect();
      return event.clientX < bounds.left || event.clientX > bounds.right ||
        event.clientY < bounds.top || event.clientY > bounds.bottom;
    };
    dialog.addEventListener('pointerdown', (event) => {
      pointerStartedOutside = event.target === dialog && isOutside(event);
    }, { signal });
    dialog.addEventListener('click', (event) => {
      if (pointerStartedOutside && event.target === dialog && isOutside(event)) close();
      pointerStartedOutside = false;
    }, { signal });

    trigger.hidden = false;
    return () => {
      if (dialog.open) dialog.close();
      restorePage(false);
      controller.abort();
      trigger.hidden = true;
    };
  }

  function init(root) {
    if (instances.has(root)) return;
    const hero = root.closest('[data-maison-pdp-hero]');
    const mainSwiper = hero?.querySelector('[data-maison-main-gallery]')?.swiper;
    instances.set(root, initHowItWorks(root, mainSwiper));
  }

  function initAll(scope = document) {
    scope.querySelectorAll('[maison-test-id="MC_PDP_SEEHOWITWORKS_KLEONE"]').forEach(init);
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[maison-test-id="MC_PDP_SEEHOWITWORKS_KLEONE"]').forEach((root) => {
      instances.get(root)?.();
      instances.delete(root);
    });
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }
})();
